import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CalendarDays,
  CheckCircle2,
  Mail,
  Moon,
  Sparkles,
  Sun,
  Tag,
  UserRound,
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const requesterOptions = [
  { value: "hr", label: "Talent & HR" },
  { value: "faculty", label: "Faculty / Admissions" },
  { value: "student", label: "Student / Peer" },
  { value: "media", label: "Press / Media" },
  { value: "other", label: "Other" },
];

const domainOptions = [
  "Brand Strategy",
  "Luxury Marketing",
  "Merchandising",
  "Digital & CRM",
  "Retail Operations",
  "Clienteling",
  "Experiential",
  "Market Research",
];

const ResumePage = ({ nightMode, setNightMode }) => {
  const initialForm = useMemo(
    () => ({
      requesterType: "",
      requestorName: "",
      email: "",
      domains: [],
      company: "",
      notes: "",
    }),
    []
  );

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [recentRequest, setRecentRequest] = useState(null);

  const toggleDomain = (domain) => {
    setFormData((prev) => {
      const exists = prev.domains.includes(domain);
      return {
        ...prev,
        domains: exists
          ? prev.domains.filter((item) => item !== domain)
          : [...prev.domains, domain],
      };
    });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.requesterType) nextErrors.requesterType = "Select requester type";
    if (!formData.company.trim()) nextErrors.company = "Company or institution is required";
    if (formData.domains.length === 0) nextErrors.domains = "Select at least one focus area";
    if (!formData.email.trim()) nextErrors.email = "Email helps me reach you quickly";
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validate();
    setErrors(validation);

    if (Object.keys(validation).length > 0) return;

    const snapshot = { ...formData };
    setIsSubmitting(true);

    try {
      const response = await fetch("https://formspree.io/f/xpwozapo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitting(false);
        setRecentRequest(snapshot);
        setSubmitted(true);
        setFormData(initialForm);
      } else {
        setIsSubmitting(false);
        setErrors({ submit: "Failed to submit. Please try again." });
      }
    } catch (error) {
      setIsSubmitting(false);
      setErrors({ submit: "Failed to submit. Please try again." });
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setErrors({});
    setRecentRequest(null);
  };

  return (
    <div className={`resume-page ${nightMode ? "night" : ""}`}>
      <div className="resume-gradient-backdrop" />
      <Navigation activeSection="resume" onNavigate={() => {}} nightMode={nightMode} />

      <main className="resume-main">
        <header className="resume-hero">
        <div className="resume-hero-ornament resume-hero-ornament--left" />
        <div className="resume-hero-ornament resume-hero-ornament--right" />
        <motion.div
          className="resume-hero-meta"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <span className="resume-hero-kicker">Resume</span>
          <button
            type="button"
            className="resume-hero-toggle"
            onClick={() => setNightMode(!nightMode)}
          >
            {nightMode ? <Sun size={16} /> : <Moon size={16} />}
            <span>{nightMode ? "Day" : "Night"} mode</span>
          </button>
        </motion.div>

        <motion.h1
          className="resume-hero-title"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.08 }}
        >
          Every line a lesson in <span className="resume-title-gradient">precision.</span>
        </motion.h1>

        <motion.p
          className="resume-hero-subtitle"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.15 }}
        >
          Request my résumé for a closer look at how I approach merchandising, cultural storytelling, and innovation within luxury.
        </motion.p>

        <motion.div
          className="resume-hero-stats"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.22 }}
        >
          <div>
            <span className="resume-stat-label">Learning Grounds</span>
            <span className="resume-stat-value">Paris · London · Perth</span>
          </div>
          <div>
            <span className="resume-stat-label">What keeps me up</span>
            <span className="resume-stat-value">Creative Briefs · Campaign Metrics</span>
          </div>
          <div>
            <span className="resume-stat-label">Professional Journey</span>
            <span className="resume-stat-value">Nykaa · Reliance Retail</span>
          </div>
        </motion.div>
      </header>

      <section className="resume-content">
        <motion.section
          className="resume-form-card"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="resume-form-heading">
            <div>
              <h2>Request the resume</h2>
              <p>Please share few details with me.</p>
            </div>
            <BadgeCheck size={26} />
          </div>

          <form className="resume-form" onSubmit={handleSubmit} noValidate>
            <div className="resume-field-group">
              <span className="resume-label">Who is requesting?</span>
              <div className="resume-option-grid">
                {requesterOptions.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={`resume-chip ${formData.requesterType === option.value ? "active" : ""}`}
                    onClick={() => handleChange("requesterType", option.value)}
                  >
                    <UserRound size={16} />
                    {option.label}
                  </button>
                ))}
              </div>
              {errors.requesterType ? <p className="resume-error">{errors.requesterType}</p> : null}
            </div>

            <div className="resume-field-grid">
              <label className="resume-input">
                <span>Primary contact email *</span>
                <div className="resume-input-wrapper">
                  <Mail size={16} />
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={(event) => handleChange("email", event.target.value)}
                    required
                  />
                </div>
                {errors.email ? <p className="resume-error">{errors.email}</p> : null}
              </label>

              <label className="resume-input">
                <span>Company or institution *</span>
                <div className="resume-input-wrapper">
                  <Building2 size={16} />
                  <input
                    type="text"
                    placeholder="Maison / University / Studio"
                    value={formData.company}
                    onChange={(event) => handleChange("company", event.target.value)}
                    required
                  />
                </div>
                {errors.company ? <p className="resume-error">{errors.company}</p> : null}
              </label>
            </div>

            <label className="resume-input">
              <span>Name (optional)</span>
              <div className="resume-input-wrapper">
                <Sparkles size={16} />
                <input
                  type="text"
                  placeholder="Your name / team"
                  value={formData.requestorName}
                  onChange={(event) => handleChange("requestorName", event.target.value)}
                />
              </div>
            </label>

            <div className="resume-field-group">
              <span className="resume-label">Which luxury focus areas? *</span>
              <div className="resume-option-grid resume-option-grid--wrap">
                {domainOptions.map((option) => {
                  const active = formData.domains.includes(option);
                  return (
                    <button
                      type="button"
                      key={option}
                      className={`resume-chip ${active ? "active" : ""}`}
                      onClick={() => toggleDomain(option)}
                    >
                      <Tag size={14} />
                      {option}
                    </button>
                  );
                })}
              </div>
              {errors.domains ? <p className="resume-error">{errors.domains}</p> : null}
            </div>

            <label className="resume-input">
              <span>Add context (optional)</span>
              <textarea
                rows={4}
                placeholder="Tell me about the role, timeline, or anything specific you want highlighted."
                value={formData.notes}
                onChange={(event) => handleChange("notes", event.target.value)}
              />
            </label>

            <div className="resume-actions">
              <button type="submit" className="resume-submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Request tailored resume"}
                <ArrowRight size={16} />
              </button>
              {errors.submit && <p className="resume-error">{errors.submit}</p>}
              <span className="resume-form-footnote">
                I respond within 24 hours. Need it sooner? Mention it above.
              </span>
            </div>
          </form>

          <AnimatePresence>
            {submitted ? (
              <motion.div
                className="resume-success"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.45 }}
              >
                <CheckCircle2 size={20} />
                <div>
                  <strong>Request received!</strong>
                  <p>
                    I’ll send a curated version aligning to {recentRequest?.domains?.length ? recentRequest.domains.join(", ") : "your focus"} within a day.
                    Expect additional project capsules if they support your search.
                  </p>
                </div>
                <button onClick={resetForm}>Send another</button>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.section>

        <motion.aside
          className="resume-sidebar"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.12 }}
        >
          <div className="resume-sidebar-card resume-sidebar-card--tone">
            <h3>Need a conversation first?</h3>
            <p>
              If you'd like to discuss a project, collaboration, or role before reviewing my résumé, I'm happy to connect for a short call.
            </p>
            <a className="resume-secondary-link" href="https://cal.com/surmayee-umathe" target="_blank" rel="noopener noreferrer">
              <CalendarDays size={16} /> Book a time
            </a>
          </div>
        </motion.aside>
      </section>

      </main>

      <Footer nightMode={nightMode} />
    </div>
  );
};

export default ResumePage;
